import React, { useMemo, useState, useEffect } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Copy, Radio, Eye, TrendingUp } from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/widget — Embeddable Live Score Widget generator.
 * Sponsors and partners can drop this <iframe> on their own sites to carry
 * the live scoreline. Backend endpoint: GET /api/widget/live.html
 */
export default function EmbedWidget() {
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
  const [theme, setTheme] = useState("dark");
  const [limit, setLimit] = useState(5);
  const [wide, setWide] = useState(false);

  const src = useMemo(() => `${base}/api/widget/live.html?theme=${theme}&limit=${limit}`, [base, theme, limit]);
  const height = 90 + limit * 60;
  const width = wide ? "100%" : "360";
  const embed = `<iframe src="${src}" width="${width}" height="${height}" frameborder="0" style="border:0;border-radius:14px;overflow:hidden;background:transparent" loading="lazy" title="Mara Padel League — Live"></iframe>`;

  const [stats, setStats] = useState(null);
  const [statsHours, setStatsHours] = useState(24);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/widget/analytics/summary?hours=${statsHours}`);
        if (alive) setStats(data);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [statsHours]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(embed);
      toast.success("Embed code copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white" data-testid="embed-widget-page">
      <div className="flex items-center gap-2 mb-1">
        <Radio className="w-5 h-5 text-[#F4B400]" />
        <h1 className="font-anton text-4xl uppercase tracking-tight">Live Score Widget</h1>
      </div>
      <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
        Drop this widget onto any sponsor, partner or media site to carry the Mara Padel League live scoreline. Auto-refreshes every 15 seconds, works cross-origin, no auth required.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 bg-[#0f1a15] border-white/10 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Theme</label>
            <div className="mt-2 flex gap-2">
              {["dark", "light"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  data-testid={`widget-theme-${t}`}
                  className={`px-4 py-2 rounded-lg border text-xs uppercase tracking-widest ${
                    theme === t ? "bg-[#00A86B]/20 border-[#00A86B]/60 text-[#00A86B]" : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Max live matches shown</label>
            <Input type="number" min={1} max={12} value={limit} onChange={(e) => setLimit(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
              className="mt-2 bg-black/40 border-white/10 text-white max-w-[120px]"
              data-testid="widget-limit-input" />
          </div>
          <div>
            <label className="flex items-center gap-3 text-xs text-zinc-300">
              <Switch checked={wide} onCheckedChange={setWide} data-testid="widget-wide-toggle" />
              Full-width iframe (100%) instead of fixed 360px
            </label>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Embed code</label>
            <textarea
              readOnly
              value={embed}
              className="mt-2 w-full h-32 p-3 rounded-lg bg-black/50 border border-white/10 text-xs font-mono text-zinc-200"
              data-testid="widget-embed-textarea"
              onFocus={(e) => e.target.select()}
            />
            <Button onClick={copy} className="mt-2 bg-[#F4B400] text-black font-bold uppercase tracking-widest hover:bg-[#e5aa00]" data-testid="widget-copy-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Embed Code
            </Button>
          </div>
        </Card>

        <Card className="p-5 bg-[#0f1a15] border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-zinc-400" />
            <h2 className="font-anton text-lg uppercase tracking-tight">Live Preview</h2>
          </div>
          <div className={`rounded-lg overflow-hidden border border-white/10 ${theme === "light" ? "bg-white" : "bg-black"}`}>
            <iframe
              key={src}
              src={src}
              width={wide ? "100%" : 360}
              height={height}
              frameBorder="0"
              style={{ border: 0, background: "transparent", display: "block" }}
              title="Widget preview"
              data-testid="widget-preview-iframe"
            />
          </div>
          <p className="mt-3 text-[11px] text-zinc-500">
            Preview polls the same public endpoint your sponsors' pages will hit. If nothing is live, the widget shows an empty state.
          </p>
        </Card>
      </div>

      {/* ── Analytics ─────────────────────────────────────────────── */}
      <Card className="mt-6 p-5 bg-[#0f1a15] border-white/10" data-testid="widget-analytics">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#00A86B]" />
          <h2 className="font-anton text-lg uppercase tracking-tight">Reach Analytics</h2>
          <div className="ml-auto flex gap-1">
            {[{h: 24, l: "24h"}, {h: 168, l: "7d"}, {h: 720, l: "30d"}].map((o) => (
              <button
                key={o.h}
                onClick={() => setStatsHours(o.h)}
                data-testid={`widget-stats-window-${o.l}`}
                className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border ${
                  statsHours === o.h ? "bg-[#00A86B]/20 border-[#00A86B]/60 text-[#00A86B]" : "bg-black/30 border-white/10 text-zinc-400"
                }`}>{o.l}</button>
            ))}
          </div>
        </div>
        {!stats ? (
          <div className="text-xs text-zinc-500">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="widget-stats-tiles">
              <StatTile label="Impressions" value={stats.impressions} accent="green" />
              <StatTile label="Clicks" value={stats.clicks} accent="gold" />
              <StatTile label="CTR" value={`${stats.ctr}%`} accent="red" />
              <StatTile label="Unique Sites" value={stats.unique_referrers} accent="neutral" />
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Top Referrers</div>
              {stats.top_referrers && stats.top_referrers.length ? (
                <div className="space-y-1">
                  {stats.top_referrers.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-white/10 bg-black/30 text-xs" data-testid={`widget-referrer-${i}`}>
                      <div className="w-5 text-center text-zinc-500">#{i + 1}</div>
                      <div className="flex-1 truncate text-zinc-200 font-mono">{r.referrer}</div>
                      <div className="text-[#F4B400] font-bold">{r.count}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-zinc-500 italic">No embeds have loaded the widget yet. Ship the embed code to a sponsor site to start collecting data.</div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function StatTile({ label, value, accent = "neutral" }) {
  const colors = {
    green: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
    gold: "text-[#F4B400] border-[#F4B400]/30 bg-[#F4B400]/5",
    red: "text-red-400 border-red-500/30 bg-red-500/5",
    neutral: "text-white border-white/10 bg-black/30",
  };
  return (
    <div className={`p-3 rounded-lg border ${colors[accent]}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-3xl leading-none mt-1">{value}</div>
    </div>
  );
}
